import { AssistantMessage, ChatMessage, ToolMessage, UserMessage } from '../types'
import { hasPartialThinkingContent } from './chat'

export type MessageGroup = {
  id: string
  type: 'single' | 'grouped'
  // Groups are seeded by a user or assistant message; tool messages only ever
  // ride along inside an assistant sequence — the types prove it so consumers
  // never re-assert it with casts.
  primaryMessage: UserMessage | AssistantMessage
  submessages?: Array<AssistantMessage | ToolMessage>
  hasThinkingContent?: boolean
  isThinkingStreaming?: boolean
}

// Prefer the client-minted id so group keys survive the optimistic→confirmed
// id swap; fall back to the backend id for history messages. When a message
// has neither, callers fall back to a positional `idx-` key — a deliberate
// exception to the no-index-keys rule: it only guards the no-identity edge
// case, and a minted uuid would change on every grouping pass.
export const messageGroupKey = (message: ChatMessage): string | undefined => message.clientId ?? message.id

// The loop only continues on tool calls, so text-only ends a turn; the next assistant message is a new one even with no user message between (system-injected prompts are dropped).
const endsTurn = (message: AssistantMessage): boolean =>
  typeof message.content === 'string' && message.content.trim().length > 0 && !message.toolCalls?.length

const buildGroups = (messages: Array<ChatMessage>): Array<MessageGroup> => {
  const groups: Array<MessageGroup> = []
  let i = 0

  while (i < messages.length) {
    const message = messages[i]

    if (message.role === 'user') {
      groups.push({
        id: `user-${messageGroupKey(message) ?? `idx-${i}`}`,
        type: 'single',
        primaryMessage: message,
      })
      i++
    } else if (message.role === 'assistant') {
      // Collect consecutive assistant/tool messages — `message` itself is the
      // first, so the sequence is never empty.
      const sequenceStartIndex = i
      const assistantToolSequence: Array<AssistantMessage | ToolMessage> = []

      while (i < messages.length) {
        const currentMessage = messages[i]
        if (currentMessage.role !== 'assistant' && currentMessage.role !== 'tool') break
        assistantToolSequence.push(currentMessage)
        i++
        if (currentMessage.role === 'assistant' && endsTurn(currentMessage)) break
      }

      // Last assistant message is primary; the sequence starts with one, so
      // the search always lands (the `?? message` fallback is for the types).
      let lastAssistant: AssistantMessage | undefined
      let lastAssistantIndex = -1
      for (let index = assistantToolSequence.length - 1; index >= 0; index -= 1) {
        const candidate = assistantToolSequence[index]
        if (candidate.role === 'assistant') {
          lastAssistant = candidate
          lastAssistantIndex = index
          break
        }
      }
      const primaryMessage = lastAssistant ?? message

      const hasThinkingContent =
        (primaryMessage.thinkingContent && primaryMessage.thinkingContent.trim().length > 0) ||
        (typeof primaryMessage.content === 'string' && hasPartialThinkingContent(primaryMessage.content))
      const isThinkingStreaming = primaryMessage.isThinkingStreaming

      if (assistantToolSequence.length > 1) {
        const submessages = assistantToolSequence.filter(
          (_, groupedMessageIndex) => groupedMessageIndex !== lastAssistantIndex,
        )

        // Key by the first message with any identity; the positional fallback
        // guarantees uniqueness within one list even for all-undefined groups —
        // the literal key `assistant-undefined` can never reach the list.
        const groupedKey = assistantToolSequence.map(messageGroupKey).find((key) => key !== undefined)
        groups.push({
          id: `assistant-${groupedKey ?? `idx-${sequenceStartIndex}`}`,
          type: 'grouped',
          primaryMessage,
          submessages: submessages.length > 0 ? submessages : undefined,
          hasThinkingContent: !!hasThinkingContent,
          isThinkingStreaming: !!isThinkingStreaming,
        })
      } else {
        groups.push({
          id: `assistant-${messageGroupKey(primaryMessage) ?? `idx-${sequenceStartIndex}`}`,
          type: 'single',
          primaryMessage,
          hasThinkingContent: !!hasThinkingContent,
          isThinkingStreaming: !!isThinkingStreaming,
        })
      }
    } else {
      i++ // Skip other message types
    }
  }

  return groups
}

const sameMessages = (a: Array<ChatMessage> | undefined, b: Array<ChatMessage> | undefined): boolean => {
  if (a === b) return true
  if (!a || !b || a.length !== b.length) return false
  return a.every((message, index) => message === b[index])
}

// A group's derived fields read only from its own messages, so a group whose id,
// primary message and submessages are all reference-identical to the previous
// pass is the same group — hand back the previous object.
const sameGroup = (a: MessageGroup, b: MessageGroup): boolean =>
  a.id === b.id &&
  a.type === b.type &&
  a.primaryMessage === b.primaryMessage &&
  sameMessages(a.submessages, b.submessages)

const groupingsByMessages = new WeakMap<Array<ChatMessage>, Array<MessageGroup>>()
// Reuse candidates from the previous pass, scoped per conversation so two
// mounted lists grouping alternately don't evict each other's entries. Reuse is
// gated on reference identity (sameGroup), so a stale entry can only cost a
// rebuild, never produce a wrong group. LRU-capped so closed chats' messages
// don't stay reachable through their last grouping for the whole session.
const LAST_GROUPS_CAPACITY = 8
const lastGroupsByConversation = new Map<string, Array<MessageGroup>>()

/**
 * Group messages into the rows the list renders.
 *
 * Memoized twice over, because every streaming delta replaces the messages
 * array: repeating a call on the same array returns the same result, and a
 * rebuilt array reuses the previous pass's group objects for every turn whose
 * messages did not change. Without the second half, each delta hands every
 * historical row a fresh `group` prop and defeats its memo. Correctness does not
 * depend on either cache — both only avoid rebuilding an identical value — so
 * batching writes upstream just makes them fire less often.
 */
export const groupCompletedMessages = (messages: Array<ChatMessage>): Array<MessageGroup> => {
  const memoized = groupingsByMessages.get(messages)
  if (memoized) return memoized

  const groups = buildGroups(messages)

  const conversationId = messages[0]?.conversation_id ?? ''
  const lastGroups = lastGroupsByConversation.get(conversationId)
  if (lastGroups) {
    const priorById = new Map(lastGroups.map((group) => [group.id, group]))
    for (let index = 0; index < groups.length; index += 1) {
      const prior = priorById.get(groups[index].id)
      if (prior && sameGroup(prior, groups[index])) {
        groups[index] = prior
      }
    }
  }

  groupingsByMessages.set(messages, groups)
  // Delete-then-set keeps insertion order as recency order for the LRU cap.
  lastGroupsByConversation.delete(conversationId)
  lastGroupsByConversation.set(conversationId, groups)
  if (lastGroupsByConversation.size > LAST_GROUPS_CAPACITY) {
    const oldest = lastGroupsByConversation.keys().next().value
    if (oldest !== undefined) lastGroupsByConversation.delete(oldest)
  }
  return groups
}
